import { useCallback, useEffect, useState } from 'react';
import GoogleLogo from 'src/images/pngs/soc-sim/google.png';
import KpmgLogo from 'src/images/pngs/soc-sim/kpmg.png';
import TravelPerkLogo from 'src/images/pngs/soc-sim/travelperk.png';
import { StyledCarouselContainer, StyledCarouselTrack, StyledCompanyLogo, StyledContainer, StyledDot, StyledDotsContainer, StyledHeading, StyledMetric, StyledStatCard, StyledStatContent, StyledStatDescription, StyledStatsContainer, StyledStatTitle, StyledTestimonialAuthor, StyledTestimonialCard, StyledTestimonialContent, StyledTestimonialHeader, StyledTestimonialQuote, StyledTestimonialsWrapper } from './trusted-by-section.styles';
import type { StatCardProps, TestimonialCardProps } from './trusted-by-section.types';
const STATS_DATA: StatCardProps[] = [{
  metric: '48%',
  title: 'Faster responses to threats',
  description: 'Teams handle incidents faster after structured training.'
}, {
  metric: '32%',
  title: 'Higher true-positive rate',
  description: 'Teams achieve higher accuracy in identifying real threats.'
}, {
  metric: '30 days',
  title: 'Junior analyst ramp up time',
  description: 'Juniors become productive in their first month, easing pressure on senior staff.'
}];
const TESTIMONIALS_DATA: TestimonialCardProps[] = [{
  quote: "I'm personally a massive fan of TryHackMe - I've been using the platform since it first came out! I only wish I had more time to complete more rooms. I use TryHackMe for personal development and thoroughly enjoy incorporating it into my work responsibilities. I was incredibly excited to follow the opportunity for a customer-facing CTF event, and the results have spoken for themselves!",
  userName: 'Keith Manville',
  userCompany: 'Google Security',
  companyLogoSrc: GoogleLogo,
  companyLogoAlt: 'Google'
}, {
  quote: "TryHackMe labs have a brilliant balance of theory and practice — explaining how cyber tools work and showcasing them in interactive real-world environments. It's a fun, straightforward platform with clear guidance, so it's easy to ups-kill independently. Bite-sized content is especially useful for training in between other priorities.",
  userName: 'Spokesperson for KPMG',
  userCompany: '',
  companyLogoSrc: KpmgLogo,
  companyLogoAlt: 'KPMG'
}, {
  quote: "TryHackMe is great to assign labs that align to the topic we've talked about that week, and team progress is easily trackable. The biggest benefit is the hands-on, guided experience, so employees can be walked through complex themes. The team values the ability to be led through a topic rather than dropped into the deep end.",
  userName: 'Spokesperson for TravelPerk',
  userCompany: '',
  companyLogoSrc: TravelPerkLogo,
  companyLogoAlt: 'TravelPerk',
  logoWidth: 23
}];
const StatCard = ({
  metric,
  title,
  description
}: StatCardProps) => <StyledStatCard data-sentry-element="StyledStatCard" data-sentry-component="StatCard" data-sentry-source-file="trusted-by-section.tsx">
    <StyledMetric data-sentry-element="StyledMetric" data-sentry-source-file="trusted-by-section.tsx">{metric}</StyledMetric>
    <StyledStatContent data-sentry-element="StyledStatContent" data-sentry-source-file="trusted-by-section.tsx">
      <StyledStatTitle data-sentry-element="StyledStatTitle" data-sentry-source-file="trusted-by-section.tsx">{title}</StyledStatTitle>
      <StyledStatDescription data-sentry-element="StyledStatDescription" data-sentry-source-file="trusted-by-section.tsx">{description}</StyledStatDescription>
    </StyledStatContent>
  </StyledStatCard>;
const TestimonialCard = ({
  quote,
  userName,
  userCompany,
  companyLogoSrc,
  companyLogoAlt,
  logoWidth
}: TestimonialCardProps) => <StyledTestimonialCard data-sentry-element="StyledTestimonialCard" data-sentry-component="TestimonialCard" data-sentry-source-file="trusted-by-section.tsx">
    <StyledTestimonialHeader data-sentry-element="StyledTestimonialHeader" data-sentry-source-file="trusted-by-section.tsx">
      <StyledCompanyLogo src={companyLogoSrc} alt={companyLogoAlt} $logoWidth={logoWidth} data-sentry-element="StyledCompanyLogo" data-sentry-source-file="trusted-by-section.tsx" />
      {/* <StyledIndustryTag>[Industry]</StyledIndustryTag> */}
    </StyledTestimonialHeader>
    <StyledTestimonialContent data-sentry-element="StyledTestimonialContent" data-sentry-source-file="trusted-by-section.tsx">
      <StyledTestimonialQuote data-sentry-element="StyledTestimonialQuote" data-sentry-source-file="trusted-by-section.tsx">"{quote}"</StyledTestimonialQuote>
      <StyledTestimonialAuthor data-sentry-element="StyledTestimonialAuthor" data-sentry-source-file="trusted-by-section.tsx">
        {userName}
        {userCompany && `, ${userCompany}`}
      </StyledTestimonialAuthor>
    </StyledTestimonialContent>
  </StyledTestimonialCard>;
export const TrustedBySection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const cardsPerSlide = 2;
  const totalSlides = TESTIMONIALS_DATA.length - cardsPerSlide + 1;
  const goToNextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  }, [totalSlides]);
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(goToNextSlide, 8000);
    return () => clearInterval(interval);
  }, [goToNextSlide, isPaused]);
  const handleSlideChange = (index: number) => {
    setCurrentSlide(index);
    setIsPaused(true);
    const timeout = setTimeout(() => setIsPaused(false), 10000);
    return () => clearTimeout(timeout);
  };
  return <StyledContainer data-sentry-element="StyledContainer" data-sentry-component="TrustedBySection" data-sentry-source-file="trusted-by-section.tsx">
      <StyledHeading data-sentry-element="StyledHeading" data-sentry-source-file="trusted-by-section.tsx">Trusted by 1000+ security teams worldwide</StyledHeading>

      <StyledStatsContainer data-sentry-element="StyledStatsContainer" data-sentry-source-file="trusted-by-section.tsx">
        {STATS_DATA.map(stat => <StatCard key={stat.title} {...stat} />)}
      </StyledStatsContainer>

      <StyledTestimonialsWrapper data-sentry-element="StyledTestimonialsWrapper" data-sentry-source-file="trusted-by-section.tsx">
        <StyledCarouselContainer data-sentry-element="StyledCarouselContainer" data-sentry-source-file="trusted-by-section.tsx">
          <StyledCarouselTrack $currentSlide={currentSlide} data-sentry-element="StyledCarouselTrack" data-sentry-source-file="trusted-by-section.tsx">
            {TESTIMONIALS_DATA.map(testimonial => <TestimonialCard key={testimonial.userName} {...testimonial} />)}
          </StyledCarouselTrack>
        </StyledCarouselContainer>
      </StyledTestimonialsWrapper>

      <StyledDotsContainer data-sentry-element="StyledDotsContainer" data-sentry-source-file="trusted-by-section.tsx">
        {Array.from({
        length: totalSlides
      }, (_, index) => index).map(slideIndex => <StyledDot key={`slide-${slideIndex}`} $isActive={currentSlide === slideIndex} onClick={() => handleSlideChange(slideIndex)} aria-label={`Go to slide ${slideIndex + 1}`} />)}
      </StyledDotsContainer>
    </StyledContainer>;
};